/**
 * Manually create a product. Creates a 1-line expense invoice with a small
 * amount so the product appears immediately in the Products list. Extracted
 * from Products.jsx (Feb 2026 refactor). Also warns if the product exists.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export function AddProductManualDialog({ existingNames, baseCurrency, lang, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [vendor, setVendor] = useState("");
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [subcategory, setSubcategory] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [catsRes, subsRes, vendorsRes] = await Promise.all([
          api.get("/categories"),
          api.get("/product-subcategories"),
          api.get("/vendors/suggestions").catch(() => ({ data: [] })),
        ]);
        // Iter 82.7l — manual products can only belong to the two system-
        // locked categories: `food_cost` (or any category the tenant has
        // classified as kind=food_cost) and `limpieza`. Cleans up the
        // dropdown noise and matches the accounting rule the owner asked
        // for.
        const raw = catsRes.data || [];
        const allowed = raw.filter(
          (c) => c.kind === "food_cost" || (c.name || "").toLowerCase() === "limpieza",
        );
        setCategories(allowed);
        const fc = allowed.find((c) => c.kind === "food_cost");
        setCategory(fc?.name || allowed[0]?.name || "");
        setSubcategories(subsRes.data || []);
        const vs = (vendorsRes.data?.suggestions || vendorsRes.data || []).map((v) => (typeof v === "string" ? v : v.vendor)).filter(Boolean);
        setVendorSuggestions(vs);
      } catch (_) { /* silent */ }
    })();
  }, []);

  const trimmedName = name.trim().toLowerCase();
  const isDup = trimmedName && existingNames.has(trimmedName);
  const totalPreview = (parseFloat(qty) || 0) * (parseFloat(unitPrice) || 0);

  const submit = async () => {
    if (!trimmedName) {
      toast.error(lang === "es" ? "Nombre requerido" : "Name required");
      return;
    }
    if (!category) {
      toast.error(lang === "es" ? "Selecciona categoría" : "Pick a category");
      return;
    }
    const up = parseFloat(unitPrice);
    if (isNaN(up) || up < 0) {
      toast.error(lang === "es" ? "Precio inválido" : "Invalid price");
      return;
    }
    const q = parseFloat(qty) || 1;
    setSaving(true);
    try {
      const subtotal = parseFloat((q * up).toFixed(2));
      await api.post("/invoices", {
        vendor: vendor.trim() || "Manual",
        type: "expense",
        category,
        date,
        subtotal,
        tax: 0,
        amount: subtotal,
        currency: baseCurrency,
        line_items: [{
          name: trimmedName,
          qty: q,
          unit_price: up,
          unit: unit || null,
          total: subtotal,
        }],
        notes: "Producto añadido manualmente",
        // Iter 82.7l — marker so /invoices (Gastos) can hide these stub
        // records by default. They still feed the /productos aggregation.
        source: "manual_product",
      });
      if (subcategory) {
        try {
          await api.post("/products/bulk-categorize", {
            product_names: [trimmedName],
            category: subcategory,
          });
        } catch (_) { /* non-blocking */ }
      }
      toast.success(lang === "es" ? "Producto añadido" : "Product added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent data-testid="add-product-manual-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Nuevo producto" : "New product"}</DialogTitle>
          <DialogDescription>
            {lang === "es"
              ? "Crea un producto manualmente. Se registrará como una factura de gasto de una línea para que aparezca en la lista."
              : "Create a product manually. Registered as a 1-line expense invoice so it appears in the list."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nombre del producto" : "Product name"}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={lang === "es" ? "ej. tomate" : "e.g. tomato"}
              autoFocus
              data-testid="add-product-name"
            />
            {isDup && (
              <p className="text-[10px] flex items-center gap-1 text-amber-600" data-testid="add-product-dup-warning">
                <AlertTriangle size={10} />
                {lang === "es"
                  ? "Este producto ya existe. Se añadirá otra ocurrencia."
                  : "This product already exists. Another occurrence will be added."}
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Cantidad" : "Qty"}</Label>
              <Input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="font-mono-num" data-testid="add-product-qty" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Ud</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="kg, l, ud..." data-testid="add-product-unit" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Precio ud" : "Unit price"}</Label>
              <Input type="number" step="0.0001" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="font-mono-num" data-testid="add-product-unit-price" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Proveedor" : "Vendor"}</Label>
              <Input list="add-product-vendor-list" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder={lang === "es" ? "opcional" : "optional"} data-testid="add-product-vendor" />
              <datalist id="add-product-vendor-list">
                {vendorSuggestions.map((v) => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Fecha" : "Date"}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="font-mono-num" data-testid="add-product-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Categoría gasto" : "Expense category"}</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm" data-testid="add-product-category">
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>{c.label_es || c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Subcategoría (opcional)" : "Subcategory (optional)"}</Label>
              <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)} className="h-9 w-full rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm" data-testid="add-product-subcategory">
                <option value="">—</option>
                {subcategories.map((sc) => (
                  <option key={sc.name} value={sc.name}>{sc.label_es || sc.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="text-xs text-[var(--text-muted)] px-2 py-1.5 rounded bg-[var(--surface-hover)]/50 flex items-center justify-between">
            <span>{lang === "es" ? "Total línea" : "Line total"}</span>
            <span className="font-mono-num font-semibold text-[var(--text-primary)]">
              {isFinite(totalPreview) ? totalPreview.toLocaleString("es-ES", { style: "currency", currency: baseCurrency, minimumFractionDigits: 2, useGrouping: "always" }) : "—"}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="add-product-save">
            {saving ? "…" : (lang === "es" ? "Añadir" : "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
